import React, { Suspense, useState, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

interface AnimatedSuspenseProps {
  /**
   * The loading fallback to display while the lazy children are loading.
   * Should be one of VendorLoadingFallback, AdminLoadingFallback, or
   * PageLoadingFallback — each already handles their own entrance animation
   * via AnimatePresence internally.
   */
  fallback: React.ReactNode;
  children: React.ReactNode;
}

/**
 * A Suspense wrapper that adds a smooth 200 ms fade-out exit animation to the
 * fallback when lazy content finishes loading.
 *
 * ## Architecture
 *
 * ```
 * AnimatedSuspense (position: relative, minHeight: 60vh)
 * ├── AnimatePresence
 * │     └── motion.div  ← overlay (position: absolute, inset: 0)
 * │           └── {fallback}  ← e.g. VendorLoadingFallback
 * └── Suspense (fallback: null)
 *       ├── LoadedSignal  ← fires onLoaded() once lazy children resolve
 *       └── {children}   ← the real page content
 * ```
 *
 * ## Why this works
 *
 * 1. `isLoading` starts `true` → the animated overlay is shown over the
 *    content area. Children start loading in the background via Suspense.
 * 2. `LoadedSignal` is co-located inside the Suspense boundary, so it
 *    suspends alongside the lazy children and mounts exactly when they do.
 * 3. On mount, `LoadedSignal` calls `onLoaded()` → `isLoading` becomes false.
 * 4. `AnimatePresence` detects child removal and plays `exit: { opacity: 0 }`
 *    over 200 ms before unmounting the overlay.
 * 5. During those 200 ms the real content is already rendered and visible
 *    beneath the fading overlay (overlay is absolutely positioned → no layout
 *    shift; content sits in normal flow below).
 *
 * ## Reduced motion
 *
 * `transition.duration` is set to 0.2 s normally. The motion.div automatically
 * respects `prefers-reduced-motion: reduce` via framer-motion's built-in
 * `MotionConfig reducedMotion="user"` — if that is set globally, the animation
 * is skipped. If not, the fallback itself disables its internal CSS keyframe
 * animations (SceneContainer `@media prefers-reduced-motion`).
 */

function LoadedSignal({ onLoaded }: { onLoaded: () => void }) {
  // Guard against React StrictMode double-invocation and concurrent re-renders.
  const calledRef = useRef(false);

  useEffect(() => {
    if (!calledRef.current) {
      calledRef.current = true;
      onLoaded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

export function AnimatedSuspense({ fallback, children }: AnimatedSuspenseProps) {
  const [isLoading, setIsLoading] = useState(true);

  return (
    // Relative container so the absolute overlay stays within this section.
    // minHeight matches the fallback components (60vh) so there's no height
    // collapse while the overlay is animating out.
    <div style={{ position: 'relative', minHeight: '60vh' }}>
      {/*
       * Persistent AnimatePresence — lives outside Suspense so it is never
       * unmounted by React's Suspense mechanism. When isLoading flips to false,
       * AnimatePresence plays the exit animation before removing the motion.div.
       */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            key="animated-suspense-overlay"
            // Overlay sits on top of the content area without disturbing layout.
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            // No entrance here — the fallback component handles its own entrance.
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            {fallback}
          </motion.div>
        )}
      </AnimatePresence>

      {/*
       * The real Suspense boundary. `fallback={null}` because our animated
       * overlay above is the visible loading state. LoadedSignal suspends
       * alongside the lazy children and fires onLoaded() once they resolve.
       */}
      <Suspense fallback={null}>
        <LoadedSignal onLoaded={() => setIsLoading(false)} />
        {children}
      </Suspense>
    </div>
  );
}
